/**
 * Shared timeframe utilities for chart windows and x-axis tick configs.
 *
 * Per-feature copies in mars-live, mars-portfolio and mars-forecasts are
 * left in place; they migrate to this module feature-by-feature.
 */

/** Superset of every timeframe key used by feature code. */
export type Timeframe =
  | '0.5y'
  | '1y'
  | '2y'
  | '3y'
  | '5y'
  | '10y'
  | '20y'
  | '30y'
  | 'all';

/** Years subtracted from the end date for each timeframe. `'all'` is unbounded. */
const TIMEFRAME_OFFSET_MONTHS: Record<Exclude<Timeframe, 'all'>, number> = {
  '0.5y': 6,
  '1y': 12,
  '2y': 24,
  '3y': 36,
  '5y': 60,
  '10y': 120,
  '20y': 240,
  '30y': 360,
};

/**
 * Approximate years span for a timeframe, used by tick-config branches that
 * scale with data range rather than the raw key. `'all'` is `Infinity` so it
 * falls into the widest tick bucket.
 */
export const TIMEFRAME_YEARS: Record<Timeframe, number> = {
  '0.5y': 0.5,
  '1y': 1,
  '2y': 2,
  '3y': 3,
  '5y': 5,
  '10y': 10,
  '20y': 20,
  '30y': 30,
  all: Number.POSITIVE_INFINITY,
};

const ISO_EPOCH = '1900-01-01';

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function toDate(d: string | Date): Date {
  return d instanceof Date ? new Date(d.getTime()) : new Date(d);
}

/**
 * Inclusive `[start, end]` window for the given timeframe, anchored at
 * `endDate`. `'all'` returns `['1900-01-01', endIso]` to mirror the
 * "show everything" convention used by long-history charts.
 *
 * Both bounds are ISO `YYYY-MM-DD` strings.
 */
export function getWindowRange(
  endDate: string | Date,
  tf: Timeframe,
): [string, string] {
  const end = toDate(endDate);
  const endIso = toIso(end);
  if (tf === 'all') return [ISO_EPOCH, endIso];

  const start = new Date(end.getTime());
  start.setMonth(start.getMonth() - TIMEFRAME_OFFSET_MONTHS[tf]);
  return [toIso(start), endIso];
}

export interface TickConfig {
  tickformat: string;
  dtick: string;
}

/**
 * X-axis tick format / spacing for the given timeframe. Buckets follow the
 * union of branches found in mars-live (`xaxisForTimeframe`), mars-portfolio
 * (`getXAxisTickConfig`) and mars-forecasts (`buildLongHistoryXAxisConfig`):
 *
 *   - >= 25y    -> `%Y` every 5 years (M60)
 *   - 15..24y   -> `%Y` every 2 years (M24)
 *   - 4..14y    -> `%Y` every year    (M12)
 *   - < 4y      -> `%b-%y` every 2 months (M2)
 */
export function tickConfigForTimeframe(tf: Timeframe): TickConfig {
  const years = TIMEFRAME_YEARS[tf];
  if (years >= 25) return { tickformat: '%Y', dtick: 'M60' };
  if (years >= 15) return { tickformat: '%Y', dtick: 'M24' };
  if (years >= 4) return { tickformat: '%Y', dtick: 'M12' };
  return { tickformat: '%b-%y', dtick: 'M2' };
}

/**
 * Slice `{date, ...}` rows to the timeframe window ending at `endDate`.
 * Rows with `date` outside `[start, end]` are dropped. `'all'` returns the
 * input unchanged. Input order is preserved.
 */
export function sliceByTimeframe<T extends { date: string }>(
  rows: readonly T[],
  endDate: string | Date,
  tf: Timeframe,
): T[] {
  if (tf === 'all') return rows.slice();
  const [start, end] = getWindowRange(endDate, tf);
  return rows.filter((r) => r.date >= start && r.date <= end);
}

/**
 * Selectable timeframes shared by every long-history chart in the
 * MARS forecasts feature (ER / Volatility tabs). Values are a subset of
 * `Timeframe`; the literal type is preserved so callers using the narrower
 * feature-local `Timeframe` continue to type-check.
 */
export const TIMEFRAME_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: '1y', label: '1Y' },
  { value: '2y', label: '2Y' },
  { value: '5y', label: '5Y' },
  { value: '10y', label: '10Y' },
  { value: '30y', label: '30Y' },
] as const satisfies ReadonlyArray<{ value: Timeframe; label: string }>;

